import type { TFunction } from 'i18next';

import { MFA_FACTOR, type MfaFactor, SMS_MFA_FEATURE_FLAG } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSText } from '@repo/sds/SDSText';
import { useFeatureFlag } from '@repo/ui/hooks/use-feature-flag';

import { MfaMethodPicker } from './mfa-method-picker';
import type { MfaPickerMethod } from './mfa-method-picker-utils';
import type { MfaPasskeyCredentialItem } from './mfa-passkey-manage';
import type { OauthProfile } from './mfa-settings-dialogs';
import { MfaSettingsPage } from './mfa-settings-page';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

export function MfaChooseMethodDialog({
  accountPrimary,
  enrolled,
  isMobile,
  isTablet,
  oauthProfile,
  passkeyCredentials,
  smsPhoneMasked,
  totpAppName,
  onAccountPrimaryChange,
  onAddEnrolledFactor,
  onCancel,
  onChooseMethod,
  onDisable,
  onManageEmail,
  onManagePasskey,
  onManageSms,
  onManageTotp,
  onOpenSmsSetup,
  t,
}: {
  accountPrimary?: string;
  enrolled: readonly string[];
  isMobile: boolean;
  isTablet: boolean;
  oauthProfile: OauthProfile;
  passkeyCredentials: readonly MfaPasskeyCredentialItem[];
  smsPhoneMasked?: string | null;
  totpAppName?: string | null;
  onAccountPrimaryChange: (method: MfaFactor) => void;
  onAddEnrolledFactor: (method: MfaFactor) => void;
  onCancel: () => void;
  onChooseMethod: (method: MfaFactor) => void;
  onDisable: () => void;
  onManageEmail: () => void;
  onManagePasskey: () => void;
  onManageSms: () => void;
  onManageTotp: () => void;
  onOpenSmsSetup: () => void;
  t: TFunction<'common'>;
}) {
  const smsSetupAvailable = useFeatureFlag(SMS_MFA_FEATURE_FLAG);
  const handleSetup = (method: MfaPickerMethod) => {
    if (method === MFA_FACTOR.Sms) {
      if (enrolled.length > 0) {
        onAddEnrolledFactor(MFA_FACTOR.Sms);
        return;
      }
      onOpenSmsSetup();
      return;
    }
    if (method === MFA_FACTOR.Email && enrolled.length > 0) {
      onAddEnrolledFactor(MFA_FACTOR.Email);
      return;
    }
    if (method === MFA_FACTOR.Totp && enrolled.length > 0 && !enrolled.includes(MFA_FACTOR.Totp)) {
      onAddEnrolledFactor(MFA_FACTOR.Totp);
      return;
    }
    if (method === MFA_FACTOR.Passkey && enrolled.length > 0 && !enrolled.includes(MFA_FACTOR.Passkey)) {
      onAddEnrolledFactor(MFA_FACTOR.Passkey);
      return;
    }
    onChooseMethod(method);
  };
  const title = t('security.mfa.chooseMethodTitle');
  const picker = (
    <MfaMethodPicker
      accountPrimary={accountPrimary}
      emailHint={oauthProfile.email}
      enabled={enrolled.length > 0}
      enrolled={enrolled}
      layout={isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'}
      onAccountPrimaryChange={onAccountPrimaryChange}
      onDisable={onDisable}
      onManageEmail={onManageEmail}
      onManagePasskey={onManagePasskey}
      onManageSms={onManageSms}
      onManageTotp={onManageTotp}
      onSetup={handleSetup}
      passkeyCount={passkeyCredentials.length}
      smsPhoneMasked={smsPhoneMasked}
      smsSetupAvailable={smsSetupAvailable}
      t={t}
      totpAppName={totpAppName}
    />
  );

  if (isMobile) {
    return (
      <MfaSettingsPage
        ariaLabel={title}
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.CHOOSE_METHOD}
        onBack={onCancel}
      >
        <SDSText text={title} type="h4" />
        {picker}
      </MfaSettingsPage>
    );
  }

  return (
    <SDSDialog
      className="MfaMethodPickerDialog"
      dataTestId={MFA.CHOOSE_METHOD}
      isOpen
      onClose={onCancel}
      showCloseButton
      size={isTablet ? 'medium' : 'large'}
      title={title}
    >
      {picker}
    </SDSDialog>
  );
}
